import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import IPBoardPart from 'src/app/model/product/ip-board/ip-board-part';
import IPBoardPartType, {
  IP_BOARD_PART_TYPE_CHOICES
} from 'src/app/model/product/ip-board/ip-board-part-type';
import {
  ClientIPBoardPartFormHelper,
  CustomSignOption
} from '../helper/client-ip-board-part-form-helper';
import {
  ClientIPBoardAndPlacementHelper,
  SignBoardOrPlacementOption
} from '../helper/client-ip-board-and-placement-helper';
import {
  ProjectDisplayObservableService,
  SignSelectedEvent
} from 'src/app/services/project-display-observable.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { ClientSignSelectorHelper } from '../client-sign-selector/client-sign-selector-helper';
import { Project } from 'src/app/model/project/project';
import { AdhesivePlacementTypeChoice } from 'src/app/model/product/ip-board/ip-board-placement-type';
import { BoardTypeChoices } from 'src/app/model/product/board/board-part';
import { SignHelper } from '../../../../../../helpers/sign-helper';

@Component({
  selector: 'client-ip-custom-board-form',
  templateUrl: './client-ip-custom-board-form.component.html',
  styleUrls: ['./client-ip-custom-board-form.component.scss']
})
export class ClientIpCustomBoardFormComponent extends SubscriptionBasedComponent
  implements OnInit {
  @Input()
  ipBoardPart: IPBoardPart;
  @Input()
  project: Project;
  @Input()
  isLeftRight = false;

  @Output()
  save = new EventEmitter<any>();

  IP_BOARD_PART_TYPE_CHOICES = IP_BOARD_PART_TYPE_CHOICES;

  CUSTOM_SIGN_IP_DIMENSION_OPTIONS_ARRAY =
    ClientIPBoardPartFormHelper.CUSTOM_SIGN_IP_DIMENSION_OPTIONS_ARRAY;

  customSelectedRightOption: CustomSignOption;

  selectedBoardTypeOption: SignBoardOrPlacementOption;
  selectedPlacementTypeOption: SignBoardOrPlacementOption;
  selectedAdhesiveBoardPlacementOption: AdhesivePlacementTypeChoice;

  RECOMMENDED_COLLABORATIVE_SIGN_OPTION_ID =
    ClientSignSelectorHelper.RECOMMENDED_COLLABORATIVE_SIGN_OPTION_ID;
  ALTERNATIVE_COLLABORATIVE_SIGN_OPTION_ID =
    ClientSignSelectorHelper.ALTERNATIVE_COLLABORATIVE_SIGN_OPTION_ID;
  selectedSignOptionId: number;

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.ipBoardPart.id != null) {
      this.initializeChoices();
    }

    this.addSubscription(
      this.projectDisplayObservableService.getSignSelectedSubscribable(),
      (event: SignSelectedEvent) => {
        if (event.ipBoardPart === this.ipBoardPart) {
          this.selectedSignOptionId = event.selectedOptionId;
          if (event.toStandardOrToCustom) {
            this.resetSelections();
          }
        }
      }
    );
    this.addSubscription(
      this.projectDisplayObservableService.getResetLeftRightSubscribable(),
      (event: boolean) => {
        if (
          event &&
          this.ipBoardPart.otherIPBoardPartType != null &&
          this.ipBoardPart.otherIPBoardPartType.ordinal ==
            IP_BOARD_PART_TYPE_CHOICES.ADHESIVE_BOARD.ordinal
        ) {
          this.resetSelections();
        }
      }
    );
  }

  private initializeChoices(): void {
    if (this.ipBoardPart.sign == null) {
      this.customSelectedRightOption = ClientIPBoardPartFormHelper.getInitialSelectedRightOption(
        this.ipBoardPart
      );
    }
    this.selectedBoardTypeOption = ClientIPBoardAndPlacementHelper.getInitialSelectedBoardOption(
      this.ipBoardPart
    );
    if (ClientIPBoardAndPlacementHelper.isAdhesiveBoard(this.ipBoardPart)) {
      this.selectedPlacementTypeOption = ClientIPBoardAndPlacementHelper.getInitialSelectedAdhesiveBoardPlacementOption(
        this.ipBoardPart
      );
    } else {
      this.selectedPlacementTypeOption = ClientIPBoardAndPlacementHelper.getInitialSelectedIPBoardPlacementOption(
        this.ipBoardPart
      );
    }
    this.selectedSignOptionId = ClientSignSelectorHelper.getSelectedSignOptionId(
      this.ipBoardPart,
      this.project.collaborative
    );
  }

  setOtherIPBoardPartType(ipBoardPartType: IPBoardPartType): void {
    const previousSelected = this.ipBoardPart.otherIPBoardPartType;
    const adhesiveBoardSelected =
      ipBoardPartType.ordinal ===
      IP_BOARD_PART_TYPE_CHOICES.ADHESIVE_BOARD.ordinal;
    this.ipBoardPart.otherIPBoardPartType = ipBoardPartType;
    if (this.customSelectedRightOption != null) {
      this.customSelectedRightOption.onSelect(this.ipBoardPart);
    }
    this.ipBoardPart.placementType = null;
    this.ipBoardPart.alternativePlacementType = null;
    this.selectedPlacementTypeOption = null;
    this.selectedAdhesiveBoardPlacementOption = null;
    if (this.shouldResetOtherDimensions(previousSelected)) {
      this.ipBoardPart.otherHeight = null;
      this.ipBoardPart.otherWidth = null;
      this.customSelectedRightOption = null;
    }
    if (adhesiveBoardSelected) {
      this.ipBoardPart.boardType = BoardTypeChoices.V;
      this.selectedBoardTypeOption =
        ClientIPBoardAndPlacementHelper.ADHESIVE_BOARD_BOARD_OPTIONS.V;
      this.projectDisplayObservableService
        .getIPBoardConfigurationChangedSubscribable()
        .next();
    } else {
      this.ipBoardPart.boardType = null;
      this.selectedBoardTypeOption = null;
    }
    this.projectDisplayObservableService.getSignSelectedSubscribable().next({
      ipBoardPart: this.ipBoardPart,
      toStandardOrToCustom: false,
      adhesiveStockSign:
        this.ipBoardPart.sign != null &&
        this.ipBoardPart.sign.id > 0 &&
        SignHelper.stockSignIsAdhesive(this.ipBoardPart.sign),
      selectedOptionId:
        this.ipBoardPart.sign != null ? this.ipBoardPart.sign.id : null,
      setAdhesive: true
    });
    this.save.emit();
  }

  private shouldResetOtherDimensions(
    previousSelected: IPBoardPartType
  ): boolean {
    return (
      previousSelected != null &&
      (this.ipBoardPart.otherIPBoardPartType.ordinal ===
        IP_BOARD_PART_TYPE_CHOICES.ADHESIVE_BOARD.ordinal ||
        this.ipBoardPart.otherIPBoardPartType.ordinal ===
          IP_BOARD_PART_TYPE_CHOICES.OTHER_DIMENSIONS.ordinal)
    );
  }

  setCustomSelectedRightOption(
    customSelectedRightOption: CustomSignOption
  ): void {
    customSelectedRightOption.onSelect(this.ipBoardPart);
    this.customSelectedRightOption = customSelectedRightOption;
    this.projectDisplayObservableService
      .getIPBoardConfigurationChangedSubscribable()
      .next({ ipBoardPart: this.ipBoardPart });
    this.save.emit();
  }

  otherDimensionsChanged(): void {
    this.projectDisplayObservableService
      .getIPBoardConfigurationChangedSubscribable()
      .next({ ipBoardPart: this.ipBoardPart });
    this.save.emit();
  }

  private resetSelections(): void {
    if (
      this.selectedSignOptionId ===
      ClientSignSelectorHelper.RECOMMENDED_COLLABORATIVE_SIGN_OPTION_ID
    ) {
      this.ipBoardPart.otherHeight = 140;
      this.ipBoardPart.otherWidth = 100;
      this.ipBoardPart.otherIPBoardPartType =
        IP_BOARD_PART_TYPE_CHOICES.IP_100_140;
    } else {
      this.ipBoardPart.otherHeight = null;
      this.ipBoardPart.otherWidth = null;
      this.ipBoardPart.otherIPBoardPartType = null;
    }
    this.projectDisplayObservableService
      .getIPBoardConfigurationChangedSubscribable()
      .next({ ipBoardPart: this.ipBoardPart });
  }
}
